import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Edit3,
  X,
} from "lucide-react";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    refSignupBonusType: "PERCENT",
    refSignupBonusValue: 10,
    refTxnFixed: 2,
    refTxnOnTypes: ["deposit", "withdraw"],
  });

  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isEditing, setIsEditing] = useState(false);

  const API_URL = "https://api.kestren.com/api/settings";

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  const fetchSettings = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axios.get(API_URL, authHeaders);
      setSettings(res.data);
      setOriginalSettings(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to load settings",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axios.put(API_URL, settings, authHeaders);
      setMessage({
        type: "success",
        text: res.data.message || "Settings updated successfully",
      });
      setOriginalSettings(settings);
      setIsEditing(false);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to update settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(originalSettings);
    setIsEditing(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleArrayToggle = (type) => {
    if (!isEditing) return;
    setSettings((prev) => {
      const exists = prev.refTxnOnTypes.includes(type);
      return {
        ...prev,
        refTxnOnTypes: exists
          ? prev.refTxnOnTypes.filter((t) => t !== type)
          : [...prev.refTxnOnTypes, type],
      };
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-10">
      <div className="max-w-3xl mx-auto">

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Referral Settings
              </h1>
              <p className="text-gray-600 mt-2 text-base">
                Configure referral bonuses and commissions
              </p>
            </div>

            <div className="flex items-center gap-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="
                  flex items-center justify-center gap-2
                  w-full sm:w-auto
                  px-5 py-3 
                  bg-gray-800 text-white
                  rounded-xl 
                  hover:bg-gray-900 
                  transition-all shadow-md
                  whitespace-nowrap        /* 🔥 Prevent text from going to next line */
              "
                >
                  <Edit3 className="w-5 h-5" />
                  Edit Settings
                </button>

              ) : (
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-5 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              )}

              <button
                onClick={fetchSettings}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-all shadow-md disabled:opacity-70"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Message */}
          {message.text && (
            <div
              className={`flex items-center gap-3 px-6 py-4 mb-6 rounded-2xl text-base font-medium shadow-sm ${message.type === "error"
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}
            >
              {message.type === "error" ? (
                <AlertCircle className="w-6 h-6 flex-shrink-0" />
              ) : (
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-gray-700" />
            </div>
          ) : (
            <div className="space-y-8">

              {/* Signup Bonus Type */}
              <div>
                <label className="block text-gray-700 font-semibold mb-3 text-base">
                  Signup Bonus Type
                </label>
                <select
                  value={settings.refSignupBonusType}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      refSignupBonusType: e.target.value,
                    })
                  }
                  className={`w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600 outline-none transition-all text-base font-medium ${!isEditing ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"
                    }`}
                >
                  <option value="PERCENT">Percent (%)</option>
                  <option value="FLAT">Flat Amount</option>
                </select>
              </div>

              {/* Signup Bonus Value */}
              <div>
                <label className="block text-gray-700 font-semibold mb-3 text-base">
                  Signup Bonus Value
                </label>
                <input
                  type="number"
                  disabled={!isEditing}
                  value={settings.refSignupBonusValue}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      refSignupBonusValue: Number(e.target.value),
                    })
                  }
                  className={`w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600 outline-none transition-all text-base font-medium ${!isEditing ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"
                    }`}
                />
              </div>

              {/* Transaction Fixed Commission */}
              <div>
                <label className="block text-gray-700 font-semibold mb-3 text-base">
                  Per Transaction Commission (Fixed Amount)
                </label>
                <input
                  type="number"
                  disabled={!isEditing}
                  value={settings.refTxnFixed}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      refTxnFixed: Number(e.target.value),
                    })
                  }
                  className={`w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600 outline-none transition-all text-base font-medium ${!isEditing ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"
                    }`}
                />
              </div>

              {/* Apply Commission On */}
              <div>
                <label className="block text-gray-700 font-semibold mb-4 text-base">
                  Apply Commission On
                </label>
                <div className="flex gap-4 flex-wrap">
                  {["deposit", "withdraw"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      disabled={!isEditing}
                      onClick={() => handleArrayToggle(type)}
                      className={`px-6 py-3 rounded-xl border text-base font-medium transition-all shadow-sm ${settings.refTxnOnTypes.includes(type)
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        } ${!isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              {isEditing && (
                <div className="pt-6 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full py-4 flex items-center justify-center gap-3 bg-gray-800 text-white rounded-2xl font-bold hover:bg-gray-900 transition-all shadow-lg disabled:opacity-70"
                  >
                    {saving ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Save className="w-6 h-6" />
                    )}
                    {saving ? "Saving Changes..." : "Save Changes"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}